import React, { useState, useEffect, useCallback } from 'react';
import './SmartOLTPanel.css';

const SmartOLTPanel = ({ onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [selectedOLT, setSelectedOLT] = useState('all');
  const [onus, setOnus] = useState([]);
  const [olts, setOlts] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState('checking');
  
  // Modal de autorización
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [selectedONU, setSelectedONU] = useState(null);
  const [authName, setAuthName] = useState('');
  const [authorizing, setAuthorizing] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [authSuccess, setAuthSuccess] = useState(false);

  // Cargar datos
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      
      // Probar conexión primero
      const testResponse = await fetch('/api/smartolt/test', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const testResult = await testResponse.json();
      
      if (!testResult.success) {
        setConnectionStatus('error');
        setError(testResult.error || 'Error de conexión con SmartOLT');
        setLoading(false);
        return;
      }
      
      setConnectionStatus('connected');

      // Cargar estadísticas
      const statsResponse = await fetch('/api/smartolt/stats', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const statsResult = await statsResponse.json();

      if (statsResult.success) {
        setStats(statsResult.stats);
        setOlts(statsResult.stats.olts || []);
        setOnus(statsResult.stats.onus || []);
      } else {
        setError(statsResult.error || 'Error cargando datos');
      }

    } catch (err) {
      console.error('Error:', err);
      setError(err.message);
      setConnectionStatus('error');
    } finally {
      setLoading(false);
    }
  }, []);

  // Cargar ONUs por OLT
  const loadONUsByOLT = useCallback(async (oltId) => {
    if (oltId === 'all') {
      loadData();
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`/api/smartolt/unauthorized-onus/${oltId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();

      if (result.success) {
        setOnus(result.onus || []);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [loadData]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Manejar cambio de OLT
  const handleOLTChange = (e) => {
    const oltId = e.target.value;
    setSelectedOLT(oltId);
    loadONUsByOLT(oltId);
  };

  // Refrescar datos
  const handleRefresh = () => {
    if (selectedOLT === 'all') {
      loadData();
    } else {
      loadONUsByOLT(selectedOLT);
    }
  };

  // Abrir modal de autorización
  const handleAuthorizeClick = (onu) => {
    setSelectedONU(onu);
    setAuthName('');
    setAuthError(null);
    setAuthSuccess(false);
    setShowAuthModal(true);
  };

  // Autorizar ONU
  const handleAuthorize = async () => {
    if (!authName.trim()) {
      setAuthError('Por favor, ingresa un nombre para la ONU');
      return;
    }

    try {
      setAuthorizing(true);
      setAuthError(null);
      
      const token = localStorage.getItem('token');
      
      const response = await fetch('/api/smartolt/authorize-onu', {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sn: selectedONU.sn,
          oltId: selectedONU.oltId,
          board: selectedONU.board,
          port: selectedONU.port,
          ponType: selectedONU.ponType || 'gpon',
          onuTypeName: selectedONU.onuTypeName,  // NOMBRE del tipo (ej: HG8145V5)
          name: authName.toUpperCase() // Nombre en mayúsculas
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        setAuthSuccess(true);
        // Recargar datos después de 2 segundos
        setTimeout(() => {
          setShowAuthModal(false);
          handleRefresh();
        }, 2000);
      } else {
        setAuthError(result.error || 'Error al autorizar la ONU');
      }
    } catch (err) {
      setAuthError(err.message);
    } finally {
      setAuthorizing(false);
    }
  };

  // Formatear fecha
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString('es-DO');
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="smartolt-panel">
      <div className="smartolt-header">
        <div className="smartolt-title">
          <span className="smartolt-icon">📡</span>
          <h2>SmartOLT - ONUs No Autorizadas</h2>
        </div>
        <div className="smartolt-actions">
          <button 
            className="btn-refresh" 
            onClick={handleRefresh}
            disabled={loading}
          >
            🔄 {loading ? 'Cargando...' : 'Actualizar'}
          </button>
          <button className="btn-close" onClick={onClose}>✕</button>
        </div>
      </div>

      {/* Estado de conexión */}
      <div className={`connection-status status-${connectionStatus}`}>
        {connectionStatus === 'checking' && '🔄 Verificando conexión...'}
        {connectionStatus === 'connected' && '✅ Conectado a SmartOLT'}
        {connectionStatus === 'error' && '❌ Error de conexión'}
      </div>

      {/* Estadísticas */}
      {stats && (
        <div className="smartolt-stats">
          <div className="stat-card">
            <div className="stat-value">{stats.totalOLTs}</div>
            <div className="stat-label">OLTs</div>
          </div>
          <div className="stat-card highlight">
            <div className="stat-value">{stats.unauthorizedONUs}</div>
            <div className="stat-label">ONUs No Autorizadas</div>
          </div>
        </div>
      )}

      {/* Filtro por OLT */}
      <div className="smartolt-filters">
        <label>Filtrar por OLT:</label>
        <select value={selectedOLT} onChange={handleOLTChange}>
          <option value="all">Todas las OLTs</option>
          {olts.map(olt => (
            <option key={olt.id} value={olt.id}>
              {olt.name || `OLT ${olt.id}`} (ID: {olt.id})
            </option>
          ))}
        </select>
      </div>

      {/* Error */}
      {error && (
        <div className="smartolt-error">
          <span>⚠️</span> {error}
        </div>
      )}

      {/* Tabla de ONUs */}
      <div className="smartolt-table-container">
        {loading ? (
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Cargando ONUs...</p>
          </div>
        ) : onus.length === 0 ? (
          <div className="no-data">
            <span className="no-data-icon">✅</span>
            <p>No hay ONUs no autorizadas</p>
          </div>
        ) : (
          <table className="smartolt-table">
            <thead>
              <tr>
                <th>Serial Number</th>
                <th>OLT</th>
                <th>Board/Port</th>
                <th>Tipo</th>
                <th>Descubierta</th>
                <th>Acción</th>
              </tr>
            </thead>
            <tbody>
              {onus.map((onu, index) => (
                <tr key={onu.id || index}>
                  <td className="sn-cell">
                    <code>{onu.sn || '-'}</code>
                  </td>
                  <td>
                    <span className="olt-badge">
                      {onu.oltName || `OLT ${onu.oltId}`}
                    </span>
                  </td>
                  <td>{onu.board}/{onu.port}</td>
                  <td>{onu.type || '-'}</td>
                  <td>{formatDate(onu.discoveredAt)}</td>
                  <td>
                    <button 
                      className="btn-authorize"
                      onClick={() => handleAuthorizeClick(onu)}
                      title="Autorizar ONU"
                    >
                      ✅ Autorizar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer */}
      <div className="smartolt-footer">
        <span>Total: {onus.length} ONU(s) no autorizada(s)</span>
        <span className="domain-info">
          📍 fibernet-rd.smartolt.com
        </span>
      </div>

      {/* Modal de Autorización */}
      {showAuthModal && selectedONU && (
        <div className="auth-modal-overlay">
          <div className="auth-modal">
            <div className="auth-modal-header">
              <h3>✅ Autorizar ONU</h3>
              <button 
                className="auth-modal-close" 
                onClick={() => setShowAuthModal(false)}
                disabled={authorizing}
              >
                ✕
              </button>
            </div>
            
            <div className="auth-modal-body">
              {authSuccess ? (
                <div className="auth-success">
                  <span className="success-icon">🎉</span>
                  <h4>¡ONU Autorizada!</h4>
                  <p>La ONU ha sido autorizada exitosamente</p>
                </div>
              ) : (
                <>
                  {/* Info de la ONU */}
                  <div className="onu-info-grid">
                    <div className="onu-info-item">
                      <label>Serial Number:</label>
                      <code>{selectedONU.sn}</code>
                    </div>
                    <div className="onu-info-item">
                      <label>OLT:</label>
                      <span>{selectedONU.oltName || `OLT ${selectedONU.oltId}`}</span>
                    </div>
                    <div className="onu-info-item">
                      <label>Board/Port:</label>
                      <span>{selectedONU.board}/{selectedONU.port}</span>
                    </div>
                    <div className="onu-info-item">
                      <label>VLAN:</label>
                      <span className="vlan-auto">120 (Automático)</span>
                    </div>
                  </div>

                  {/* Campo de nombre */}
                  <div className="auth-name-field">
                    <label>Nombre de la ONU *</label>
                    <input
                      type="text"
                      value={authName}
                      onChange={(e) => setAuthName(e.target.value)}
                      placeholder="Ej: CLIENTE-JUAN-PEREZ"
                      disabled={authorizing}
                      autoFocus
                    />
                    <small>El nombre se guardará en MAYÚSCULAS</small>
                  </div>

                  {/* Error */}
                  {authError && (
                    <div className="auth-error">
                      ⚠️ {authError}
                    </div>
                  )}
                </>
              )}
            </div>

            {!authSuccess && (
              <div className="auth-modal-footer">
                <button 
                  className="btn-cancel"
                  onClick={() => setShowAuthModal(false)}
                  disabled={authorizing}
                >
                  Cancelar
                </button>
                <button 
                  className="btn-confirm-auth"
                  onClick={handleAuthorize}
                  disabled={authorizing || !authName.trim()}
                >
                  {authorizing ? '⏳ Autorizando...' : '✅ Autorizar ONU'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SmartOLTPanel;
